import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslations } from 'ilenia';

import { tablets } from 'styling';
import { Arrow } from 'components/Arrows';
import { GREY_DARKER, BLUE_SATURATED } from 'constants';

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const SpacedArrow = styled(Arrow)`
  margin: 0 5px;
`;

const PrevPage = styled.div`
  order: 2;
  margin: 0 30px 0 0;
  color: ${({ theme }) => theme.colors.greyDarker};

  @media (${tablets}) {
    order: 1;
  }
`;

const NextPage = styled.div`
  order: 3;
  margin: 0 0 0 30px;
  color: ${({ theme }) => theme.colors.greyDarker};
`;

const ListPage = styled.ul`
  order: 1;
  padding: 0;
  width: 100%;
  list-style: none;
  margin: 0 0 32px;
  text-align: center;
  white-space: nowrap;
  overflow: auto hidden;

  @media (${tablets}) {
    order: 2;
    width: auto;
    margin: 0 30px;
  }
`;

const ListPageItem = styled.li`
  ${({ theme, selected }) => `
    padding: 0 8px;
    border-radius: 4px;
    display: inline-block;

    & a {
      color: ${theme.colors.greyDarkest};
    }

    ${
      selected &&
      `
    margin: 0 4px 0 0;
    background-color: ${theme.colors.blueSaturated};

    & a {
      color: ${theme.colors.white};
      cursor: initial;
    }
  `
    }
`};
`;

export const Pagination = ({
  total,
  pageSize,
  pageCurrent = 1,
  pageLimit,
  onPageClick,
}) => {
  const [translations] = useTranslations();
  const pageAmount = Math.ceil(total / pageSize);
  pageLimit = pageLimit < pageAmount ? pageLimit : pageAmount - 1; // insure that pagination never go out it's limit
  let pageStart;
  if (pageCurrent - Math.ceil(pageLimit / 2) <= 1) {
    pageStart = 1;
  } else if (pageCurrent + Math.ceil(pageLimit / 2) > pageAmount) {
    pageStart = pageAmount - pageLimit;
  } else {
    pageStart = pageCurrent - Math.ceil(pageLimit / 2);
  }

  const pageElements = useMemo(() => {
    let els = [];
    for (
      let i = pageStart, j = 0;
      j <= pageLimit && i <= pageAmount;
      i++, j++
    ) {
      els.push(
        <ListPageItem
          key={i}
          selected={pageCurrent === i}
          aria-current={pageCurrent === i ? 'page' : undefined}
        >
          <a
            href={i > 1 ? `?page=${i}` : ``}
            onClick={(event) => {
              event.preventDefault();
              onPageClick(i);
            }}
            title={`Page ${i}`}
          >
            {i}
          </a>
        </ListPageItem>
      );
    }

    return els;
  }, [pageCurrent, pageAmount]);

  return pageAmount > 1 ? (
    <Wrapper>
      <PrevPage disabled={pageCurrent === 1}>
        {pageCurrent !== 1 ? (
          <a
            href={pageCurrent - 1 > 1 ? `?page=${pageCurrent - 1}` : ``}
            onClick={(event) => {
              event.preventDefault();
              onPageClick(pageCurrent - 1);
            }}
          >
            <SpacedArrow color={BLUE_SATURATED} direction="left" />
            {translations['pagination/prev']}
          </a>
        ) : (
          <span tabIndex="-1" aria-disabled="true">
            <SpacedArrow color={GREY_DARKER} direction="left" />
            {translations['pagination/prev']}
          </span>
        )}
      </PrevPage>
      <ListPage>
        {pageElements}
        {pageStart + pageLimit < pageAmount && <ListPageItem>...</ListPageItem>}
      </ListPage>
      <NextPage disabled={pageCurrent === pageAmount}>
        {pageCurrent < pageAmount ? (
          <a
            href={`?page=${pageCurrent + 1}`}
            onClick={(event) => {
              event.preventDefault();
              onPageClick(pageCurrent + 1);
            }}
          >
            {translations['pagination/next']}
            <SpacedArrow color={BLUE_SATURATED} direction="right" />
          </a>
        ) : (
          <span tabIndex="-1" aria-disabled="true">
            {translations['pagination/next']}
            <SpacedArrow color={GREY_DARKER} direction="right" />
          </span>
        )}
      </NextPage>
    </Wrapper>
  ) : (
    ''
  );
};
